import { Component, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import {
  SuiModal,
  ComponentModalConfig,
  ModalSize,
  DatepickerMode,
} from '@richardlt/ng2-semantic-ui';
import { SettingsService } from '../../services/settings.service';
import { AppFilter } from '@app/shared/models';

interface NotificationSettings {
  notificationType: string;
  description: string;
  emailAddress?: string;
  emailSubject?: string;
  name?: String;
  slackChannel?: String;
}
interface IAddNotificationModalContext {
  data: any;
  title?: string;
}
@Component({
  selector: 'app-add-notification-modal',
  templateUrl: './add-notification-modal.component.html',
  styleUrls: ['./add-notification-modal.component.scss'],
})
export class AddNotificationModalComponent implements OnInit {
  success: any;
  error: any;
  notificationForm: FormGroup;
  selectForm: FormGroup;
  pending: boolean;
  extraData: any;
  formSubmitAttempt: boolean;
  selectedType: string | null = null;
  isSecondPage: boolean = false;
  channels: any[];
  constructor(
    public modal: SuiModal<IAddNotificationModalContext, void, void>,
    private fb: FormBuilder,
    private notificationSvc: SettingsService
  ) {
    this.selectForm = this.fb.group({
      notificationType: ['', Validators.required],
    });

    this.selectForm.get('notificationType').valueChanges.subscribe((value) => {
      this.selectedType = value;
    });

    this.notificationForm = this.fb.group({
      name: ['', Validators.required],
      email: ['', [Validators.required, Validators.email]],
      subject: ['', Validators.required],
      description: ['', Validators.required],
      channel: ['', Validators.required],
    });

    this.extraData = this.modal.context.data;
    this.notificationForm.patchValue(this.extraData);
    this.getChannel(1);

    if (!this.modal.context.data.isAdd) {
      this.isSecondPage = true;
      this.selectedType = this.extraData.type || 'slack';
    }
    // if (!this.extraData.isAdd) {
    //   this.getChannelFromModal();
    // }
  }

  ngOnInit(): void {
    this.selectForm.get('notificationType').valueChanges.subscribe((type) => {
      const emailControl = this.notificationForm.get('email');
      const subjectControl = this.notificationForm.get('subject');

      if (type === 'email') {
        emailControl.setValidators([Validators.required, Validators.email]);
        subjectControl.setValidators([Validators.required]);
      } else {
        emailControl.clearValidators();
        subjectControl.clearValidators();
      }

      emailControl.updateValueAndValidity();
      subjectControl.updateValueAndValidity();
    });

    this.notificationForm.get('channel').valueChanges.subscribe((value) => {
      console.log('Channel changed to:', value);
    });
  }

  isFieldValid(field: string) {
    return (
      (!this.notificationForm.get(field).valid &&
        this.notificationForm.get(field).touched) ||
      (this.notificationForm.get(field).untouched && this.formSubmitAttempt)
    );
  }

  selectedChannel: any = null;
  selectChannel(channel: any) {
    this.selectedChannel = channel;
    this.notificationForm.patchValue({ channel: channel.name });
  }

  getChannelFromModal() {
    if (this.channels && this.channels.length > 0) {
      const selectedChannel = this.channels.find(
        (method: any) => method.name === this.extraData.channel
      );
      if (selectedChannel) {
        this.selectedChannel = selectedChannel;
        this.notificationForm.patchValue({
          channel: selectedChannel.name,
        });
      }
    } else {
      console.error('Channels are not loaded yet.');
    }
  }
  selectedChannels: any[] = [];

  onChannelsSelect(selectedChannels: any[]) {
    this.selectedChannels = selectedChannels;
    this.notificationForm.patchValue({
      channel: selectedChannels.map((c) => c.name).join(','),
    });
  }

  selectedChannelIds: string = '';

  toggleChannelSelection(channel: any) {
    const index = this.selectedChannels.findIndex((c) => c.id === channel.id);
    if (index === -1) {
      this.selectedChannels.push(channel);
    } else {
      this.selectedChannels.splice(index, 1);
    }
    this.selectedChannelIds = this.selectedChannels.map((c) => c.id).join(',');
    this.notificationForm.patchValue({
      channel: this.selectedChannelIds,
    });
  }
  getSelectedChannelsText(): string {
    return this.selectedChannels.map((channel) => channel.name).join(', ');
  }
  isChannelSelected(channel: any): boolean {
    return this.selectedChannels.some((c) => c.id === channel.id);
  }

  addNewSettings() {
    this.pending = true;

    if (this.notificationForm.valid || this.isFormValidForEmail()) {
      const notificationType = this.selectForm.get('notificationType').value;
      const description = this.notificationForm.get('description').value;
      const name = this.notificationForm.get('name').value;
      let settings: NotificationSettings = {
        notificationType: notificationType,
        description: description,
        name: name,
      };

      if (notificationType === 'email') {
        settings.emailAddress = this.notificationForm.get('email').value;
        settings.emailSubject = this.notificationForm.get('subject').value;
      }
      if (notificationType === 'slack') {
        settings.slackChannel = this.selectedChannels
          .map((c) => c.id)
          .join(',');
      }
      const requestBody = {
        settingsType: 'notification_settings',
        settings: JSON.stringify(settings),
      };

      this.notificationSvc.addSetting({ body: requestBody }).subscribe({
        next: (response) => {
          this.pending = false;
          this.success = 'Settings added successfully';
          this.modal.approve(undefined);
        },
        error: (error) => {
          this.pending = false;
          this.error = 'Failed to add settings';
          console.error('Error adding settings:', error);
        },
      });
    }
  }

  displayFieldCss(field: string) {
    return {
      error: this.isFieldValid(field),
    };
  }

  proceedToSecondPage() {
    if (this.selectForm.valid) {
      this.isSecondPage = true;
    }
  }
  getChannel(page: number) {
    this.pending = true;
    const filter = new AppFilter();
    filter.page = page;

    this.notificationSvc.getSlackChannel({ params: filter }).subscribe({
      next: (res: any) => {
        this.pending = false;
        this.channels = res;
        if (this.extraData && this.extraData.channel) {
          this.selectedChannels = res.filter((c) =>
            this.extraData.channel.split(',').includes(c.id)
          );
          this.notificationForm.patchValue({
            channel: this.selectedChannels.map((c) => c.id).join(','),
          });
        }
      },
      error: (error) => {
        this.pending = false;
        this.error = error.errorMessage ? error.errorMessage : error;
      },
    });
  }

  editSetting() {
    this.pending = true;

    const notificationType = this.selectForm.get('notificationType').value;
    const description = this.notificationForm.get('description').value;
    const name = this.notificationForm.get('name').value;
    let settings: NotificationSettings = {
      notificationType: notificationType,
      description: description,
      name: name,
    };
    if (this.modal.context.data.showEmailField == false) {
      settings.notificationType = 'slack';
      settings.slackChannel = this.selectedChannels.map((c) => c.id).join(',');
    }
    if (this.modal.context.data.showEmailField == true) {
      settings.notificationType = 'email';
      settings.emailAddress = this.notificationForm.get('email').value;
      settings.emailSubject = this.notificationForm.get('subject').value;
    }

    const requestBody = {
      settingsType: 'notification_settings',
      settings: JSON.stringify(settings),
    };

    this.notificationSvc
      .editSetting({
        body: requestBody,
        settingsId: this.modal.context.data.settingId,
      })
      .subscribe({
        next: (response) => {
          this.pending = false;
          this.success = 'Settings Edited successfully';
          setTimeout(() => {
            this.modal.approve(undefined);
          }, 1000);
        },
        error: (error) => {
          this.pending = false;
          this.error = 'Failed to edit settings';
          console.error('Error adding settings:', error);
        },
      });
  }
  isFormValidForEmail() {
    return (
      this.notificationForm.get('name').valid &&
      this.notificationForm.get('email').valid &&
      this.notificationForm.get('subject').valid &&
      this.notificationForm.get('description').valid
    );
  }
}

export class addNotificationModal extends ComponentModalConfig<
  IAddNotificationModalContext,
  void,
  void
> {
  constructor(title: string, data: any, size: ModalSize = ModalSize.Small) {
    super(AddNotificationModalComponent, { title, data });

    this.isClosable = false;
    this.transitionDuration = 200;
    this.transition = 'slide left';
    this.size = size;
    this.isFullScreen = false;
    this.isInverted = false;
  }
}
